
import { z } from "zod";

enum ANALYSIS_SEVERITY {
  CRITICAL = "critical",
  CAUTION = "caution",
  OPPORTUNITY = "opportunity",
  BEST_PRACTICE = "best_practice"
}

enum ANALYSIS_CATEGORY {
  CASH_FLOW = "cash_flow",
  DEBT = "debt",
  RETURNS = "returns",
  VALUATION = "valuation",
  GROWTH = "growth",
  EXPENSES = "expenses",
  EXIT = "exit",
  REFINANCE = "refinance",
  RESERVES = "reserves",
  OPERATIONS = "operations",
  RENOVATION = "renovation",
  GENERAL = "general"
}

const AnalysisSourceRefSchema = z.object({
  metric_key: z.string(),
  metric_label: z.string(),
  value: z.string(),
  unit_of_measure: z.string(),
  period: z.string(),
  source_sheet: z.string(),
  source_address: z.string(),
  nav_named_range: z.string()
});

const AnalysisNavigationSchema = z.object({
  sheet: z.string(),
  address: z.string(),
  named_range: z.string()
});

const AnalysisFindingSchema = z.object({
  finding_id: z.string(),
  severity: z.enum(ANALYSIS_SEVERITY),
  category: z.enum(ANALYSIS_CATEGORY),
  title: z.string(),
  description: z.string(),
  impact: z.string(),
  recommendation: z.string(),
  benchmark_context: z.string(),
  value_summary: z.string(),
  confidence: z.number(),
  source_refs: z.array(AnalysisSourceRefSchema),
  navigation: AnalysisNavigationSchema,
});

const AiDealSummary = z.object({
  name: z.string(),
  unit_count: z.number(),
  purchase_price: z.number(),
  exit_price: z.number(),
  entry_cap: z.number(),
  exit_cap: z.number(),
  cash_on_cash: z.number(),
  leveraged_irr: z.number(),
  avg_annual_return: z.number(),
  equity_multiple: z.number()
});

const AiDealAnalysisResultSchema = z.object({
  analysis_summary: z.object({
    total_findings: z.number(),
    critical_issues: z.number(),
    caution_flags: z.number(),
    opportunities: z.number(),
    best_practices: z.number().optional(),
    overall_assessment: z.string().optional(),
  }),
  deal_summary: AiDealSummary,
  findings: z.array(AnalysisFindingSchema)
});

type AiDealAnalysisResult = z.infer<typeof AiDealAnalysisResultSchema>;
type AnalysisFinding = z.infer<typeof AnalysisFindingSchema>;

type SortedAiDealAnalyisResult = {
  totalFindings: number;
  overallAssessment: string;
  dealSummary: z.infer<typeof AiDealSummary>;
  findings: Record<ANALYSIS_SEVERITY, AnalysisFinding[]>
}

export {
  AiDealAnalysisResult,
  AiDealAnalysisResultSchema,
  ANALYSIS_CATEGORY,
  ANALYSIS_SEVERITY, AnalysisFinding, SortedAiDealAnalyisResult
};

